import React, { useState, useEffect, useMemo } from "react";
import { ClipLoader } from "react-spinners";
import { Card, CardHeader, CardBody, Row, Col } from "reactstrap";
import moment from "moment";
import api from "../../../../../services/api";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies";
import LogCard from "../../../../../components/LogCard/LogCard";
import LogDetailsModal from "../../../../../components/LogDetailsModal/LogDetailsModal"; // Importe o componente criado

const DynamicCards = () => {
  const { darkMode } = useTheme();
  const { empresa, transportadora, arrayParams } = useCompanies();

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState([]);
  const [selectedLogType, setSelectedLogType] = useState("");
  const auxDtInicial = useMemo(
    () => moment().subtract(90, "days").startOf("month"),
    []
  );
  const auxDtFinal = useMemo(() => moment().endOf("month"), []);

  // Memoriza os objetos para que sua referência seja estável
  const logTypeNames = useMemo(
    () => ({
      naoEncontradoTMS: "Falta de Nota Fiscal",
      outros: "Outros Erros",
      divergenciaCnpj: "Divergência de CNPJ",
      remetenteNaoCadastrado: "CNPJ do Remetente",
      transportadoraNaoCadastrada: "CNPJ da Transportadora",
      naoEncontrouCteNormal: "CTe Normal não encontrado",
      naoEncontrouTagNota: "TAG Nota não encontrada",
      arquivoJaImportado: "Já importado",
    }),
    []
  );

  const logTypeDescriptions = useMemo(
    () => ({
      naoEncontradoTMS:
        "Nota fiscal listada no CTe não encontrada no sistema TMS.",
      outros: "Erros diversos não categorizados.",
      divergenciaCnpj: "Divergência encontrada entre CNPJs do CTe e da NFe.",
      remetenteNaoCadastrado: "CNPJ do Remetente não cadastrado no sistema.",
      transportadoraNaoCadastrada:
        "CNPJ da Transportadora não cadastrado no sistema.",
      naoEncontrouCteNormal:
        "Não foi encontrado CTe Normal no arquivo de CTe Complementar",
      naoEncontrouTagNota: "Não foi encontrada TAG de Nota Fiscal no CTe ",
      arquivoJaImportado:
        "CT-e já havia sido importado anteriormente para essa transportadora.",
    }),
    []
  );

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(false);

        const params = {
          dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
          dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
        };

        if (arrayParams.embarcadores) {
          params.embarcadores = arrayParams.embarcadores;
        }

        if (arrayParams.transportadoras) {
          params.transportadoras = arrayParams.transportadoras;
        }

        const response = await api.get("/dashboard/cockpit/logcte", { params });

        if (response.status === 200) {
          const rawData = response.data;
          const maxCount = Math.max(
            ...Object.values(rawData).map((item) => item.quantidade)
          );
          const formattedData = Object.entries(rawData).map(([key, value]) => ({
            key, // Adiciona o identificador original
            type: logTypeNames[key] || key, // Nome amigável
            count: value.quantidade,
            percentage: (value.quantidade / maxCount) * 100, // Baseado no valor máximo
            description: logTypeDescriptions[key] || "Descrição não disponível", // Descrição
          }));
          setLogs(formattedData);
        }
      } catch (error) {
        console.error("Erro ao carregar os logs:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    if (empresa) {
      fetchData();
    }
  }, [
    empresa,
    transportadora,
    arrayParams.embarcadores,
    arrayParams.transportadoras,
    auxDtInicial,
    auxDtFinal,
    logTypeNames,
    logTypeDescriptions,
  ]);

  const fetchLogDetails = async (logType) => {
    try {
      const params = {
        dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
        dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
        tipoLog: logType,
      };

      const response = await api.get("/dashboard/cockpit/logcte/details", {
        params,
      });

      if (response.status === 200) {
        setModalDetails(response.data);
        setSelectedLogType(logTypeNames[logType] || logType);
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Erro ao buscar os detalhes dos logs:", error);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  return (
    <>
      <Card className="card-chart">
        <CardHeader
          style={{
            backgroundColor: darkMode ? "#27293c" : "#ffffff",
          }}
        >
          <Row style={{ alignItems: "center" }}>
            <Col>
              <h4 style={{ marginBottom: "5px" }}>Resumo de Logs do CTe</h4>
              <p
                style={{
                  fontSize: "0.9rem",
                  color: darkMode ? "#cfcfcf" : "#666666",
                  marginBottom: "20px",
                }}
              >
                Monitoramento das principais ocorrências relacionadas aos CTe's
                processados.
              </p>
            </Col>
          </Row>
        </CardHeader>
        <CardBody style={{ paddingLeft: "2rem", paddingRight: "2rem" }}>
          {loading ? (
            <div style={{ textAlign: "center" }}>
              <ClipLoader size={90} color="#1d8cf8" loading={loading} />
              <h4 style={{ marginTop: "20px", color: "#999999" }}>
                Buscando dados...
              </h4>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", color: "red" }}>
              <h5>Ocorreu um erro ao buscar os dados.</h5>
            </div>
          ) : logs.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
                justifyContent: "space-between",
              }}
            >
              {logs.map((log) => (
                <LogCard
                  key={log.key}
                  type={log.type}
                  count={log.count}
                  percentage={log.percentage}
                  darkMode={darkMode}
                  description={log.description}
                  onClick={() => fetchLogDetails(log.key)}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <h5>Nenhum dado encontrado!</h5>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Usando o componente LogDetailsModal */}
      <LogDetailsModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title={`Detalhes do Log: ${selectedLogType}`}
        data={modalDetails}
      />
    </>
  );
};

export default DynamicCards;
